import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
} from '@material-ui/core';
import { useApi, alertApiRef } from '@backstage/core-plugin-api';
import {
  EditingContext,
  PipelineConfig,
  validatePipelineConfig,
  withDescriptionFallback,
  PipelinePrResult,
} from '@internal/backstage-plugin-sql-pipeline-common';

import {
  PipelineConflictError,
  sqlPipelineApiRef,
} from '../api/SqlPipelineApi';
import { PipelineForm } from './PipelineForm';
import { PipelinePreview } from './PipelinePreview';
import { HandEditsDiff } from './HandEditsDiff';
import { PrCreatedDialog } from './PrCreatedDialog';
import { TicketField, useTicket } from './TicketField';


/**
 * 파이프라인 수정 다이얼로그.
 *
 * scaffolder 템플릿을 대체한다:
 *  - `/create` 로 페이지를 이동하지 않는다
 *  - 이미 어느 파이프라인인지 아는데 다시 고르게 하지 않는다
 *  - 설정을 **카탈로그 투영이 아니라 Git** 에서 읽는다 → 조용한 되돌림 없음
 *  - 저장 직전 파일이 바뀌었으면 409 를 받아 재로드를 유도한다
 */
export const EditPipelineDialog = (props: {
  pipelineName: string;
  open: boolean;
  onClose: () => void;
}) => {
  const { pipelineName, open, onClose } = props;
  const api = useApi(sqlPipelineApiRef);
  const alertApi = useApi(alertApiRef);

  const [context, setContext] = useState<EditingContext | null>(null);
  const [config, setConfig] = useState<PipelineConfig | null>(null);
  const ticketState = useTicket();
  const { ticket } = ticketState;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [issues, setIssues] = useState<Record<string, string>>({});
  const [result, setResult] = useState<PipelinePrResult | null>(null);
  /** 생성과 같은 2 단계. 저장 전에 실제로 커밋될 파일을 보여준다. */
  const [step, setStep] = useState<'form' | 'preview'>('form');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const ctx = await api.getEditingContext(pipelineName);
      setContext(ctx);
      // 같은 파이프라인을 수정 중인 열린 PR 이 있으면 그 브랜치 버전을
      // 초기값으로 쓴다 — main 값을 쓰면 그 PR 의 변경을 덮어쓴다.
      const onBranch = ctx.openPrs.find(p => p.configOnBranch);
      setConfig(onBranch?.configOnBranch ?? ctx.config);
      setIssues({});
      setStep('form');
    } catch (e: any) {
      setLoadError(e?.message ?? '설정을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [api, pipelineName]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const activePr = context?.openPrs.find(p => p.configOnBranch);
  /** 미리보기와 저장에 쓰는 설정 — 설명이 비면 이 수정 티켓의 링크로 채운 것이다. */
  const resolved = config ? withDescriptionFallback(config, ticket) : null;

  /** 검증을 통과하면 미리보기로 넘어간다. */
  const handlePreview = () => {
    if (!ticketState.ensureValid() || !resolved) return;
    const validation = validatePipelineConfig(resolved);
    if (!validation.ok) {
      const map: Record<string, string> = {};
      for (const i of validation.issues) {
        map[i.path] = i.message;
      }
      setIssues(map);
      alertApi.post({
        message: `입력을 확인해주세요: ${validation.issues[0].message}`,
        severity: 'error',
      });
      return;
    }
    setIssues({});
    setStep('preview');
  };

  const handleSave = async () => {
    if (!context || !resolved) return;
    setSaving(true);
    try {
      // 이어서 수정 중이면 그 브랜치의 sha 가 기준이다.
      const base = activePr ? activePr.shasOnBranch : context.source.shas;
      const saved = await api.savePipeline(pipelineName, {
        ticketNumber: ticket,
        config: resolved,
        base,
      });
      setResult(saved);
    } catch (e) {
      if (e instanceof PipelineConflictError) {
        // 최신 설정이 응답에 실려 온다 — 다시 불러오지 않고 바로 반영한다.
        setContext(e.response.latest);
        setConfig(e.response.latest.config);
        // 최신 설정으로 바뀌었으니 미리보기가 낡았다 — 폼으로 되돌린다.
        setStep('form');
        alertApi.post({ message: e.response.message, severity: 'warning' });
      } else {
        alertApi.post({
          message: (e as Error)?.message ?? '저장에 실패했습니다.',
          severity: 'error',
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const canPreview = Boolean(config) && !saving && !loading;

  if (result) {
    return (
      <PrCreatedDialog
        open={open}
        onClose={() => {
          setResult(null);
          onClose();
        }}
        title="수정 PR 을 만들었습니다"
        result={result}
        outcome="정상적으로 반영되면 변경 내용이 적용됩니다."
      />
    );
  }

  return (
    // 바깥 클릭으로는 닫지 않는다 — 긴 폼을 채우다 창 밖을 한 번 누르면 입력이
    // 전부 사라졌다(사용자 지적). 닫기는 취소 버튼·Esc 로만 한다.
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') onClose();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>파이프라인 수정 — {pipelineName}</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box display="flex" alignItems="center" gridGap={8} py={4}>
            <CircularProgress size={20} />
            <Typography variant="body2">
              Git 에서 현재 설정을 불러오는 중...
            </Typography>
          </Box>
        )}

        {loadError && (
          <Box py={2}>
            <Typography color="error" variant="body2">
              {loadError}
            </Typography>
            <Button onClick={load} size="small" style={{ marginTop: 8 }}>
              다시 시도
            </Button>
          </Box>
        )}

        {!loading && !loadError && context && resolved && step === 'preview' && (
          <PipelinePreview config={resolved} />
        )}

        {!loading && !loadError && context && config && step === 'form' && (
          <>
            <Box mb={2}>
              <Typography variant="caption" color="textSecondary">
                설정 출처: {context.location.catalogRepo.owner}/
                {context.location.catalogRepo.repo} @ {context.source.ref} —{' '}
                {context.location.catalogPath}
              </Typography>
            </Box>

            {activePr && (
              <Box
                mb={2}
                p={1.5}
                borderRadius={4}
                style={{
                  border: '1px solid #f9a825',
                  backgroundColor: 'rgba(249, 168, 37, 0.08)',
                }}
              >
                <Typography variant="body2" style={{ color: '#f9a825' }}>
                  열린 PR{' '}
                  <Link href={activePr.url} target="_blank" rel="noopener">
                    #{activePr.number}
                  </Link>{' '}
                  ({activePr.branch}) 위에서 이어서 수정합니다. 폼의 초기값은
                  그 브랜치 기준입니다.
                </Typography>
              </Box>
            )}

            {context.handEdited && (
              <Box
                mb={2}
                p={1.5}
                borderRadius={4}
                style={{
                  border: '1px solid #e65100',
                  backgroundColor: 'rgba(230, 81, 0, 0.08)',
                }}
              >
                <Typography variant="body2" style={{ color: '#e65100' }}>
                  이 DAG 의 파일이 <strong>Backstage 밖에서 수정</strong>되었습니다.
                  아래 폼은 Backstage 가 아는 설정이므로, 그대로 저장하면 그 수정이
                  사라집니다. 먼저{' '}
                  <Link
                    href={`https://github.com/${context.location.dagRepo.owner}/${context.location.dagRepo.repo}/tree/${context.source.ref}/${context.location.files.dir}`}
                    target="_blank"
                    rel="noopener"
                  >
                    저장소의 파일
                  </Link>
                  을 확인해주세요.
                </Typography>
                <HandEditsDiff edits={context.handEdits ?? []} />
              </Box>
            )}

            <TicketField
              state={ticketState}
              helperText="PR 브랜치명으로 사용됩니다"
              style={{ marginBottom: 16 }}
            />

            <PipelineForm
              config={config}
              onChange={setConfig}
              errors={issues}
              mode="edit"
              // 수정에서 팀은 잠겨 있다 — 팀을 바꾸는 것은 파일 이동이고
              // 그건 삭제 후 재생성이다.
              availableTeams={[config.team_tag]}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        {step === 'preview' ? (
          <>
            <Button onClick={() => setStep('form')} disabled={saving}>
              돌아가서 수정
            </Button>
            <Box flex={1} />
            <Button onClick={onClose} disabled={saving}>
              취소
            </Button>
            <Button
              onClick={handleSave}
              color="primary"
              variant="contained"
              disabled={saving}
              startIcon={
                saving ? <CircularProgress size={16} color="inherit" /> : undefined
              }
            >
              수정 PR 생성
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>취소</Button>
            <Button
              onClick={handlePreview}
              color="primary"
              variant="contained"
              disabled={!canPreview}
            >
              바뀔 파일 보기
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
